/* JC vertical thumbnail rail — scrollable thumbnail column with ONE fixed-position
   scroll control pinned to the bottom of the rail (a footer that does NOT move as the rail
   scrolls). The control flips direction by scroll position: points DOWN while there are more
   thumbnails below the fold (click scrolls down a page); flips UP once the rail is at the
   bottom (click scrolls back to the top). Hidden entirely when nothing overflows.
   Thumbnail CLICK → main image is still handled by Dawn's media-gallery.js. */
class JcThumbRail extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.thumbnail-list');
    this.btn = this.querySelector('[data-vthumb-scroll]');
    if (!this.list) return;
    const gallery = this.closest('media-gallery');
    this.viewer = gallery ? gallery.querySelector('[id^="GalleryViewer"]') : null;
    this._up = false;

    this._sync = () => { this.syncHeight(); this.update(); };
    this.list.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', this._sync);
    this.addEventListener('keydown', (e) => this.onKey(e));
    if (this.btn) this.btn.addEventListener('click', () => this.onClick());

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => this._sync());
      if (this.viewer) this._ro.observe(this.viewer);
      this._ro.observe(this.list);
    }
    // images load async — settle a couple of times after paint
    requestAnimationFrame(() => this._sync());
    setTimeout(() => this._sync(), 500);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._sync);
    if (this._ro) this._ro.disconnect();
  }

  get vertical() {
    // vertical rail only applies on desktop; below 990 the column is hidden anyway
    return window.matchMedia('(min-width: 990px)').matches;
  }

  /* Cap the rail's height to the main image's height so a few thumbnails show and the rest
     scroll. The list flex-fills the space above the pinned control; below 990 the cap clears. */
  syncHeight() {
    if (!this.vertical || !this.viewer) { this.style.maxHeight = ''; return; }
    const h = this.viewer.getBoundingClientRect().height;
    if (h > 80) this.style.maxHeight = Math.round(h) + 'px';
  }

  pageAmount() {
    const item = this.list.querySelector('.thumbnail-list__item');
    const h = item ? item.getBoundingClientRect().height : 96;
    const gap = parseFloat(getComputedStyle(this.list).rowGap || '8') || 8;
    return (h + gap) * 3; // advance ~3 thumbnails per press
  }

  update() {
    if (!this.list || !this.btn) return;
    const el = this.list;
    const scrollable = this.vertical && el.scrollHeight > el.clientHeight + 2;
    this.btn.hidden = !scrollable;
    if (!scrollable) return;
    // near the bottom → flip to "up" (back to top); otherwise "down" (more below)
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    this._up = atBottom;
    this.btn.classList.toggle('vio-thumbrail__arrow--up', atBottom);
    this.btn.setAttribute('aria-label', atBottom ? 'Back to top' : 'Scroll to more images');
  }

  onClick() {
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = smooth ? 'smooth' : 'auto';
    if (this._up) this.list.scrollTo({ top: 0, behavior: behavior });
    else this.list.scrollBy({ top: this.pageAmount(), behavior: behavior });
  }

  onKey(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const btns = Array.from(this.list.querySelectorAll('.thumbnail-list__item button'));
    const i = btns.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'ArrowDown' ? btns[i + 1] : btns[i - 1];
    if (next) {
      next.focus();
      next.scrollIntoView({ block: 'nearest' });
    }
  }

  /* Compatibility shims for Dawn's media-gallery.js setActiveThumbnail() — it expects a horizontal
     slider (isSlideVisible + .slider). Our rail is vertical, so when a colour swatch selects a
     variant image, we scroll THAT thumbnail into view vertically here and always return true, which
     stops media-gallery from running its (horizontal) scroll and prevents the console error. */
  isSlideVisible(el, offset = 0) {
    if (!el || !this.list) return true;
    const lr = this.list.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const visible = er.top >= lr.top - offset && er.bottom <= lr.bottom + offset;
    if (!visible) {
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const target = this.list.scrollTop + (er.top - lr.top) - Math.max(0, (lr.height - er.height) / 2);
      this.list.scrollTo({ top: Math.max(0, Math.round(target)), behavior: smooth ? 'smooth' : 'auto' });
      setTimeout(() => this.update(), 350);
    }
    return true;
  }

  get slider() { return this.list; }
}

if (!customElements.get('jc-thumb-rail')) customElements.define('jc-thumb-rail', JcThumbRail);
